import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Category, CreateMainCategory, MainCategory, UpdateCategory, UpdateMainCategory } from '@mmf/contracts';
import {
  createMainCategory,
  fetchAdminCategories,
  fetchAdminMainCategories,
  updateCategory,
  updateMainCategory,
} from '../api/client';

/**
 * Administrator-only category management.
 *
 * Main categories are the one thing here with a real create form — an
 * administrator names one, and its 5 fixed sub-categories are generated
 * automatically (see MainCategoryRepository.create). Those 5 are then
 * editable the same way they always were: name, description, order, and
 * whether they're available for new topics. `key` is still not editable —
 * it's code-facing — and there's still no delete, because deleting would
 * orphan every topic in it. Deactivating hides it while leaving the history
 * readable.
 */
export function CategoryAdmin(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState('');
  const [newSortOrder, setNewSortOrder] = useState('0');

  const mainCategories = useQuery({
    queryKey: ['admin', 'main-categories'],
    queryFn: fetchAdminMainCategories,
  });

  const invalidateMainCategories = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'main-categories'] });
    await queryClient.invalidateQueries({ queryKey: ['main-categories'] });
  };

  const create = useMutation({
    mutationFn: (input: CreateMainCategory) => createMainCategory(input),
    onSuccess: async () => {
      setNewName('');
      setNewSortOrder('0');
      await invalidateMainCategories();
    },
  });

  const saveMainCategory = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateMainCategory }) => updateMainCategory(id, patch),
    onSuccess: invalidateMainCategories,
  });

  if (mainCategories.isLoading) {
    return <p role="status">Loading categories…</p>;
  }

  if (mainCategories.error || !mainCategories.data) {
    return (
      <p role="alert" data-testid="categories-error">
        Could not load the categories.
      </p>
    );
  }

  const handleCreate = (event: React.FormEvent): void => {
    event.preventDefault();
    create.mutate({ name: newName, sortOrder: Number(newSortOrder) });
  };

  return (
    <section aria-labelledby="category-admin-heading" data-testid="category-admin">
      <h3 id="category-admin-heading">Categories</h3>
      <p className="hint">
        A main category's 5 sub-categories are created for you automatically. Renaming a category
        or switching it off takes effect immediately.
      </p>

      <form onSubmit={handleCreate} className="new-topic-form" aria-labelledby="new-main-category-heading">
        <h3 id="new-main-category-heading">New main category</h3>

        {create.error && (
          <p role="alert" className="notice warning">
            Could not create that category. Try again.
          </p>
        )}

        <label htmlFor="new-main-category-name">Name</label>
        <input
          id="new-main-category-name"
          value={newName}
          maxLength={128}
          required
          onChange={(event) => setNewName(event.target.value)}
        />

        <label htmlFor="new-main-category-order">Order</label>
        <input
          id="new-main-category-order"
          type="number"
          min={0}
          max={999}
          value={newSortOrder}
          onChange={(event) => setNewSortOrder(event.target.value)}
        />

        <div className="row">
          <button
            type="submit"
            className="button"
            disabled={newName.trim() === '' || create.isPending}
            data-testid="create-main-category"
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>

      {saveMainCategory.error && (
        <p role="alert" className="notice warning" data-testid="save-error">
          Could not save that change. Try again, and let the service desk know if it persists.
        </p>
      )}

      <ul className="category-list">
        {mainCategories.data.map((mainCategory) => (
          <MainCategoryRow
            key={mainCategory.id}
            mainCategory={mainCategory}
            isSaving={saveMainCategory.isPending && saveMainCategory.variables?.id === mainCategory.id}
            onSave={(patch) => saveMainCategory.mutate({ id: mainCategory.id, patch })}
          />
        ))}
      </ul>

      {mainCategories.data.length === 0 && (
        <p className="hint">No main categories yet — create the first one above.</p>
      )}
    </section>
  );
}

interface MainCategoryRowProps {
  mainCategory: MainCategory;
  isSaving: boolean;
  onSave: (patch: UpdateMainCategory) => void;
}

function MainCategoryRow({ mainCategory, isSaving, onSave }: MainCategoryRowProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState(mainCategory.name);
  const [sortOrder, setSortOrder] = useState(String(mainCategory.sortOrder));
  const [isActive, setIsActive] = useState(mainCategory.isActive);
  const [isExpanded, setIsExpanded] = useState(false);

  const isDirty =
    name !== mainCategory.name || sortOrder !== String(mainCategory.sortOrder) || isActive !== mainCategory.isActive;

  const categories = useQuery({
    queryKey: ['admin', 'categories', mainCategory.id],
    queryFn: () => fetchAdminCategories(mainCategory.id),
    enabled: isExpanded,
  });

  const saveCategory = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateCategory }) => updateCategory(id, patch),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'categories', mainCategory.id] });
      await queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });

  const fieldId = (field: string): string => `main-category-${mainCategory.id}-${field}`;

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    onSave({ name, sortOrder: Number(sortOrder), isActive });
  };

  return (
    <li className="category-row" data-testid={`main-category-row-${mainCategory.id}`}>
      <form onSubmit={handleSubmit}>
        <fieldset>
          <legend>
            {mainCategory.name}
            {!mainCategory.isActive && <span className="badge"> hidden</span>}
          </legend>

          <label htmlFor={fieldId('name')}>Name</label>
          <input
            id={fieldId('name')}
            value={name}
            maxLength={128}
            required
            onChange={(event) => setName(event.target.value)}
          />

          <div className="row">
            <span>
              <label htmlFor={fieldId('order')}>Order</label>
              <input
                id={fieldId('order')}
                type="number"
                min={0}
                max={999}
                value={sortOrder}
                onChange={(event) => setSortOrder(event.target.value)}
              />
            </span>

            <span className="checkbox">
              <input
                id={fieldId('active')}
                type="checkbox"
                checked={isActive}
                onChange={(event) => setIsActive(event.target.checked)}
              />
              <label htmlFor={fieldId('active')}>Available for new discussions</label>
            </span>
          </div>

          <div className="row">
            <button type="submit" className="button" disabled={!isDirty || isSaving} data-testid={`save-main-category-${mainCategory.id}`}>
              {isSaving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="button secondary"
              onClick={() => setIsExpanded((current) => !current)}
              data-testid={`toggle-sub-categories-${mainCategory.id}`}
            >
              {isExpanded ? 'Hide sub-categories' : 'Show sub-categories'}
            </button>
          </div>
        </fieldset>
      </form>

      {isExpanded && (
        <div className="sub-category-list">
          {categories.isLoading && <p role="status">Loading sub-categories…</p>}

          {categories.error && (
            <p role="alert" data-testid={`sub-categories-error-${mainCategory.id}`}>
              Could not load sub-categories.
            </p>
          )}

          <ul className="category-list">
            {categories.data?.map((category) => (
              <CategoryRow
                key={category.id}
                category={category}
                isSaving={saveCategory.isPending && saveCategory.variables?.id === category.id}
                onSave={(patch) => saveCategory.mutate({ id: category.id, patch })}
              />
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

interface CategoryRowProps {
  category: Category;
  isSaving: boolean;
  onSave: (patch: UpdateCategory) => void;
}

function CategoryRow({ category, isSaving, onSave }: CategoryRowProps): React.JSX.Element {
  const [name, setName] = useState(category.name);
  const [description, setDescription] = useState(category.description ?? '');
  const [sortOrder, setSortOrder] = useState(String(category.sortOrder));
  const [isActive, setIsActive] = useState(category.isActive);

  const isDirty =
    name !== category.name ||
    description !== (category.description ?? '') ||
    sortOrder !== String(category.sortOrder) ||
    isActive !== category.isActive;

  const fieldId = (field: string): string => `category-${category.id}-${field}`;

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    onSave({
      name,
      description: description.trim() === '' ? null : description,
      sortOrder: Number(sortOrder),
      isActive,
    });
  };

  return (
    <li className="category-row">
      <form onSubmit={handleSubmit}>
        <fieldset>
          {/* The key is the stable identity, so it labels the group rather
              than being presented as something you can edit. */}
          <legend>
            {category.key}
            {!category.isActive && <span className="badge"> hidden</span>}
          </legend>

          <label htmlFor={fieldId('name')}>Name</label>
          <input
            id={fieldId('name')}
            value={name}
            maxLength={128}
            required
            onChange={(event) => setName(event.target.value)}
          />

          <label htmlFor={fieldId('description')}>
            Description <span className="hint">— the guidance people read before posting</span>
          </label>
          <textarea
            id={fieldId('description')}
            value={description}
            maxLength={1024}
            rows={2}
            onChange={(event) => setDescription(event.target.value)}
          />

          <div className="row">
            <span>
              <label htmlFor={fieldId('order')}>Order</label>
              <input
                id={fieldId('order')}
                type="number"
                min={0}
                max={999}
                value={sortOrder}
                onChange={(event) => setSortOrder(event.target.value)}
              />
            </span>

            <span className="checkbox">
              <input
                id={fieldId('active')}
                type="checkbox"
                checked={isActive}
                onChange={(event) => setIsActive(event.target.checked)}
              />
              <label htmlFor={fieldId('active')}>Available for new topics</label>
            </span>
          </div>

          <button
            type="submit"
            className="button"
            disabled={!isDirty || isSaving}
            data-testid={`save-${category.id}`}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </fieldset>
      </form>
    </li>
  );
}
